import { useEffect, useRef, useCallback, useState } from 'react';
import { initializeEcho } from './echo';

export const useWebSocket = (jwtToken, currentUserId) => {
  const echoRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const userChannelsRef = useRef(new Map());
  const conversationChannelsRef = useRef(new Map());
  const presenceChannelsRef = useRef(new Map());

  const messageHandlersRef = useRef(new Map());
  const typingHandlersRef = useRef(new Map());

  const initialize = useCallback(() => {
    if (!jwtToken || !currentUserId) {
      console.log('❌ Cannot initialize WebSocket: Missing JWT token or user ID');
      return null;
    }

    try {
      console.log('🚀 Initializing WebSocket connection...');
      
      if (echoRef.current) {
        console.log('🧹 Cleaning up existing WebSocket connection...');
        echoRef.current.disconnect();
      }

      const echo = initializeEcho(jwtToken);
      echoRef.current = echo;

      echo.connector.pusher.connection.bind('connected', () => {
        console.log('🟢 WebSocket Connected Successfully');
        setIsConnected(true);
      });

      echo.connector.pusher.connection.bind('disconnected', () => {
        console.log('🔴 WebSocket Disconnected');
        setIsConnected(false);
      });

      echo.connector.pusher.connection.bind('error', (error) => {
        console.error('❌ WebSocket Connection Error:', error);
        setIsConnected(false);
      });

      echo.connector.pusher.connection.bind('state_change', (states) => {
        console.log('🔄 WebSocket State Change:', states.previous, '→', states.current);
      });

      console.log('✅ WebSocket initialized successfully');
      return echo;
    } catch (error) {
      console.error('❌ Failed to initialize Echo:', error);
      setIsConnected(false);
      return null;
    }
  }, [jwtToken, currentUserId]);

  const subscribeToUserChannel = useCallback((onNewMessage, onTypingIndicator) => {
    if (!echoRef.current || !currentUserId) {
      console.log('❌ Cannot subscribe to user channel: No Echo instance or user ID');
      return null;
    }

    try {
      const channelName = `user.${currentUserId}`;
      console.log(`👂 Subscribing to user channel: ${channelName}`);
      
      if (userChannelsRef.current.has(channelName)) {
        echoRef.current.leave(channelName);
        userChannelsRef.current.delete(channelName);
      }

      const channel = echoRef.current.private(channelName);
      
      messageHandlersRef.current.set(channelName, onNewMessage);
      typingHandlersRef.current.set(channelName, onTypingIndicator);
      
      channel.listen('.message.sent', (event) => {
        console.log('🔔 Global message notification received:', event);
        onNewMessage?.(event);
      });

      channel.listen('.typing.indicator', (event) => {
        console.log('⌨️ Global typing indicator received:', event);
        onTypingIndicator?.(event);
      });

      channel.error((error) => {
        console.error(`❌ User channel error (${channelName}):`, error);
      });

      userChannelsRef.current.set(channelName, channel);
      console.log(`✅ Successfully subscribed to user channel: ${channelName}`);
      
      return channel;
    } catch (error) {
      console.error('❌ Failed to subscribe to user channel:', error);
      return null;
    }
  }, [currentUserId]);

  const subscribeToConversation = useCallback((conversationId, onMessageReceived, onTypingIndicator) => {
    if (!echoRef.current || !conversationId) {
      console.log('❌ Cannot subscribe to conversation: No Echo instance or conversationId');
      return null;
    }

    try {
      const channelName = `conversation.${conversationId}`;
      console.log(`📡 SUBSCRIBING TO CONVERSATION CHANNEL: ${channelName}`);
      
      if (conversationChannelsRef.current.has(channelName)) {
        echoRef.current.leave(channelName);
        conversationChannelsRef.current.delete(channelName);
      }

      const channel = echoRef.current.private(channelName);
      
      channel.subscribed(() => {
        console.log(`🎉 CONVERSATION CHANNEL SUBSCRIBED: ${channelName}`);
      });

      channel.listen('.message.sent', (event) => {
        console.log('💬 CONVERSATION MESSAGE RECEIVED (message.sent):', event);
        onMessageReceived?.(event.message || event.data || event);
      });

      channel.listen('.typing.indicator', (event) => {
        console.log('⌨️ Conversation typing indicator:', event);
        onTypingIndicator?.(event);
      });
      
      channel.error((error) => {
        console.error(`❌ CONVERSATION CHANNEL ERROR (${channelName}):`, error);
      });

      conversationChannelsRef.current.set(channelName, channel);
      console.log(`✅ CONVERSATION CHANNEL SUBSCRIPTION INITIATED: ${channelName}`);
      
      return channel;
    } catch (error) {
      console.error('❌ FAILED TO SUBSCRIBE TO CONVERSATION CHANNEL:', error);
      return null;
    }
  }, []);

  const joinPresenceChannel = useCallback((conversationId, onHere, onJoining, onLeaving) => {
    if (!echoRef.current || !conversationId) {
      console.log('❌ Cannot join presence channel: No Echo instance or conversationId');
      return null;
    }

    try {
      const baseChannelName = conversationId.startsWith('temp-') ? conversationId.substring(5) : conversationId;
      const channelName = `presence-conversation.${baseChannelName}`;
      console.log(`👥 Joining presence channel: ${channelName}`);
      
      if (presenceChannelsRef.current.has(channelName)) {
        echoRef.current.leave(channelName);
        presenceChannelsRef.current.delete(channelName);
      }

      const presenceChannel = echoRef.current.join(channelName);
      
      presenceChannel
        .here((users) => {
          console.log(`✅ Users currently online in ${channelName}:`, users);
          onHere?.(users);
        })
        .joining((user) => {
          console.log(`🟢 User joined ${channelName}:`, user);
          onJoining?.(user);
        })
        .leaving((user) => {
          console.log(`🔴 User left ${channelName}:`, user);
          onLeaving?.(user);
        })
        .error((error) => {
          console.error(`❌ Presence channel error (${channelName}):`, error);
        });

      presenceChannelsRef.current.set(channelName, presenceChannel);
      console.log(`✅ Successfully joined presence channel: ${channelName}`);
      
      return presenceChannel;
    } catch (error) {
      console.error('❌ Failed to join presence channel:', error);
      return null;
    }
  }, []);

  const leaveChannel = useCallback((channelName) => {
    if (echoRef.current) {
      echoRef.current.leave(channelName);
      
      userChannelsRef.current.delete(channelName);
      conversationChannelsRef.current.delete(channelName);
      presenceChannelsRef.current.delete(channelName);
      
      console.log(`🚫 Left channel: ${channelName}`);
    }
  }, []);

  const leaveConversation = useCallback((conversationIdOrTempId) => {
    if (!conversationIdOrTempId) return;

    const isTempId = conversationIdOrTempId.startsWith('temp-');
    const actualId = isTempId ? conversationIdOrTempId.substring(5) : conversationIdOrTempId;

    const channelsToLeave = [
      `conversation.${actualId}`,
      `presence-conversation.${actualId}`
    ];

    channelsToLeave.forEach(channelName => {
      leaveChannel(channelName);
    });

    console.log(`🚫 Left all channels for conversation: ${conversationIdOrTempId}`);
  }, [leaveChannel]);

  const checkConnected = useCallback(() => {
    return echoRef.current && echoRef.current.connector.pusher.connection.state === 'connected';
  }, []);

  const disconnect = useCallback(() => {
    if (echoRef.current) {
      console.log('🔌 Disconnecting WebSocket completely...');
      
      userChannelsRef.current.clear();
      conversationChannelsRef.current.clear();
      presenceChannelsRef.current.clear();
      messageHandlersRef.current.clear();
      typingHandlersRef.current.clear();
      
      echoRef.current.disconnect();
      echoRef.current = null;
      setIsConnected(false);
      console.log('✅ WebSocket disconnected completely');
    }
  }, []);

  return {
    initialize,
    disconnect,
    isConnected,
    checkConnected,
    
    subscribeToUserChannel,
    subscribeToConversation,
    joinPresenceChannel,
    leaveChannel,
    leaveConversation,
    
    echoInstance: echoRef.current
  };
};

export default useWebSocket;